import { NgModule, ModuleWithProviders } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';

// Modules
import { MessageModule } from '@pages/components/message/message.module';
import { MessageService } from '@pages/components/message/message.service';
import { pgSelectModule } from '../@pages/components/select/select.module';

// Directives
import { Directives } from './directives';

// Api
import { Api } from './api';

// Interceptors
import { Interceptor } from './interceptors/http.interceptors';
import { JwtInterceptor } from './interceptors/jwt.interceptors';
import { ErrorInterceptor } from './interceptors/error.interceptor';

// Services
import { Services } from './services';
import { GoogleMapComponent } from './components/google-map/google-map.component';

// Other Modules
import { AgmCoreModule } from '@agm/core';
import { ProgressModule } from '@pages/components/progress/progress.module';
import { CollapseModule, ButtonsModule } from 'ngx-bootstrap';
import { EmbedVideo } from 'ngx-embed-video';
import { LZStringModule, LZStringService } from 'ng-lz-string';
import { RouterModule } from '@angular/router';
import { PreOrderComponent } from '@pages/partbnb-component';
import { pgSelectfx } from '@pages/components/cs-select/select.module';

// Components
import { Icons } from './components/icons';
import { Images } from './components/images';
import { FooterComponent } from './components/footer/footer.component';
import { LoaderComponent } from './components/loader/loader.component';
import { PhotoGalleryComponent } from './components/photo-gallery/photo-gallery.component';
import { VideoPlayerComponent } from './components/video-player/video-player.component';
import { PhotoCarouselComponent } from './components/photo-carousel/photo-carousel.component';
import { CustomChartComponent } from './components/custom-chart/custom-chart.component';
import { VerticalTimelineComponent } from './components/vertical-timeline/vertical-timeline.component';
import { PhotoViewerComponent } from './components/photo-viewer/photo-viewer.component';
import { FormProgressBarComponent } from './components/form-progress-bar/form-progress-bar.component';
import { ImageSignupComponent } from './components/images/image-signup/image-signup.component';
import { ProgressRingComponent } from './components/progress-ring/progress-ring.component';
import { CircularProgressComponent } from './components/icons/circular-progress/circular-progress.component';
import { PersonalDetailsComponent } from './components/steps/personal-details/personal-details.component';
import { ContactInformationComponent } from './components/steps/contact-information/contact-information.component';
import { PanelDetailsComponent } from './components/pre-order/panel-details/panel-details.component';
import { PanelOptionsComponent } from './components/pre-order/panel-options/panel-options.component';
import { PreOrderDetailsComponent } from './components/steps/pre-order-details/pre-order-details.component';
import { CustomSelectComponent } from './components/custom-select/custom-select.component';
import { HeaderUserAvatarComponent } from './components/header-user-avatar/header-user-avatar.component';
import { MarketplaceIconComponent } from './components/icons/marketplace-icon/marketplace-icon.component';
import { InvestorsIconComponent } from './components/icons/investors-icon/investors-icon.component';
import { SaveIconComponent } from './components/icons/save-icon/save-icon.component';
import { OpenIconComponent } from './components/icons/open-icon/open-icon.component';
import { ReceivingIconComponent } from './components/icons/receiving-icon/receiving-icon.component';
import { BankIconComponent } from './components/icons/bank-icon/bank-icon.component';
import { InvestorIconComponent } from './components/icons/investor-icon/investor-icon.component';
import { YieldsIconComponent } from './components/icons/yields-icon/yields-icon.component';
import { LongTermIconComponent } from './components/icons/long-term-icon/long-term-icon.component';
import { DiversifyIconComponent } from './components/icons/diversify-icon/diversify-icon.component';
import { StayingIconComponent } from './components/icons/staying-icon/staying-icon.component';
import { OwnershipIconComponent } from './components/icons/ownership-icon/ownership-icon.component';
import { NumberOneIconComponent } from './components/icons/number-one-icon/number-one-icon.component';
import { NumberTwoIconComponent } from './components/icons/number-two-icon/number-two-icon.component';
import { BookingIconComponent } from './components/icons/booking-icon/booking-icon.component';
import { CheckinIconComponent } from './components/icons/checkin-icon/checkin-icon.component';
import { ListingIconComponent } from './components/icons/listing-icon/listing-icon.component';
import { OccupancyIconComponent } from './components/icons/occupancy-icon/occupancy-icon.component';
import { WebLogoIconComponent } from './components/icons/web-logo-icon/web-logo-icon.component';
import { EmailLogoIconComponent } from './components/icons/email-logo-icon/email-logo-icon.component';
import { NumberThreeIconComponent } from './components/icons/number-three-icon/number-three-icon.component';
import { NumberFourIconComponent } from './components/icons/number-four-icon/number-four-icon.component';
import { KeylobbyIconComponent } from './components/icons/keylobby-icon/keylobby-icon.component';
import { AchIconComponent } from './components/icons/ach-icon/ach-icon.component';
import { GetMeNotifiedComponent } from './components/get-me-notified/get-me-notified.component';
import { MailingComponentComponent } from './components/mailing-component/mailing-component.component';
import { HomeVideoModalComponent } from './components/home-video-modal/home-video-modal.component';
import { PrivacyPolicyComponent } from './components/modals/privacy-policy/privacy-policy.component';
import { TermsOfUseComponent } from './components/modals/terms-of-use/terms-of-use.component';
import { SignInComponent } from './components/modals/sign-in/sign-in.component';
import { SignUpSuccessComponent } from './components/modals/sign-up-success/sign-up-success.component';
import { PlaidModals } from './components/plaid';
import { IconPlusGrayComponent } from './components/icons/icon-plus-gray/icon-plus-gray.component';
import { IconTrashComponent } from './components/icons/icon-trash/icon-trash.component';
import { DragAndDropDirective } from './directives/drag-and-drop/drag-and-drop.directive';
import { InvestorVerificationComponent } from './components/modals/investor-verification/investor-verification.component';
import { IconNetWorthComponent } from './components/icons/icon-net-worth/icon-net-worth.component';
import { IconIncomeVerificationComponent } from './components/icons/icon-income-verification/icon-income-verification.component';
import { IconAccreditedLetterComponent } from './components/icons/icon-accredited-letter/icon-accredited-letter.component';
import { DragAndDropComponent } from './components/drag-and-drop/drag-and-drop.component';
import { PropertyStatusComponent } from './components/property-status/property-status.component';
import { ConfirmationComponent } from './components/modals/confirmation/confirmation.component';
import { OrderPropertyModalComponent } from './components/modals/order-property-modal/order-property-modal.component';
import { AddFundsComponent } from './components/modals/add-funds/add-funds.component';
import { TablePaginationComponent } from './components/table-pagination/table-pagination.component';
import { MyProfileComponent } from './components/my-profile/my-profile.component';
import { AccreditedInvestorsComponent } from './components/modals/accredited-investors/accredited-investors.component';
import { pgTabsModule } from '@pages/components/tabs/tabs.module';

@NgModule({
  imports: [
    CommonModule,
    HttpClientModule,
    MessageModule,
    AgmCoreModule,
    ProgressModule,
    NgbModule,
    EmbedVideo.forRoot(),
    FormsModule,
    ReactiveFormsModule,

    pgSelectModule,
    LZStringModule,
    RouterModule,
    pgSelectfx,
    ButtonsModule,
    pgTabsModule
  ],
  declarations: [
    ...Icons,
    ...Directives,
    ...Images,
    FooterComponent,
    GoogleMapComponent,
    LoaderComponent,
    PhotoGalleryComponent,
    VideoPlayerComponent,
    PhotoCarouselComponent,
    CustomChartComponent,
    VerticalTimelineComponent,
    PhotoViewerComponent,
    FormProgressBarComponent,
    ImageSignupComponent,
    ProgressRingComponent,
    CircularProgressComponent,
    PersonalDetailsComponent,
    ContactInformationComponent,
    PanelDetailsComponent,
    PanelOptionsComponent,
    PreOrderDetailsComponent,
    CustomSelectComponent,
    HeaderUserAvatarComponent,
    MarketplaceIconComponent,
    InvestorsIconComponent,
    SaveIconComponent,
    OpenIconComponent,
    ReceivingIconComponent,
    BankIconComponent,
    InvestorIconComponent,
    YieldsIconComponent,
    LongTermIconComponent,
    DiversifyIconComponent,
    StayingIconComponent,
    OwnershipIconComponent,
    NumberOneIconComponent,
    NumberTwoIconComponent,
    BookingIconComponent,
    CheckinIconComponent,
    ListingIconComponent,
    OccupancyIconComponent,
    WebLogoIconComponent,
    EmailLogoIconComponent,
    NumberThreeIconComponent,
    NumberFourIconComponent,
    KeylobbyIconComponent,
    AchIconComponent,
    GetMeNotifiedComponent,
    MailingComponentComponent,
    HomeVideoModalComponent,
    PrivacyPolicyComponent,
    TermsOfUseComponent,
    PreOrderComponent,
    SignInComponent,
    SignUpSuccessComponent,
    ...PlaidModals,
    IconPlusGrayComponent,
    IconTrashComponent,
    DragAndDropDirective,
    InvestorVerificationComponent,
    IconNetWorthComponent,
    IconIncomeVerificationComponent,
    IconAccreditedLetterComponent,
    DragAndDropComponent,
    PropertyStatusComponent,
    ConfirmationComponent,
    OrderPropertyModalComponent,
    AddFundsComponent,
    TablePaginationComponent,
    MyProfileComponent,
    AccreditedInvestorsComponent
  ],
  exports: [
    ...Icons,
    ...Directives,
    ...Images,
    FooterComponent,
    GoogleMapComponent,
    LoaderComponent,
    PhotoGalleryComponent,
    PhotoCarouselComponent,
    VideoPlayerComponent,
    CustomChartComponent,
    VerticalTimelineComponent,
    PhotoViewerComponent,
    FormProgressBarComponent,
    ProgressRingComponent,
    PersonalDetailsComponent,
    ContactInformationComponent,
    PreOrderDetailsComponent,
    CustomSelectComponent,
    HeaderUserAvatarComponent,
    GetMeNotifiedComponent,
    MailingComponentComponent,
    HomeVideoModalComponent,
    PreOrderComponent,
    DragAndDropComponent,
    PropertyStatusComponent,
    TablePaginationComponent,
    MyProfileComponent
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    MessageService,
    ...Api,
    ...Services,
    LZStringService
  ],
  entryComponents: [
    PrivacyPolicyComponent,
    TermsOfUseComponent,
    SignInComponent,
    PanelDetailsComponent,
    SignUpSuccessComponent,
    InvestorVerificationComponent,
    ConfirmationComponent,
    ...PlaidModals,
    ConfirmationComponent,
    OrderPropertyModalComponent,
    AddFundsComponent,
    AccreditedInvestorsComponent
  ]
})
export class SharedAppModule {
  static forRoot(): ModuleWithProviders {
    return { ngModule: CollapseModule, providers: [] };
  }
}
